import React, { useEffect, useMemo, useState } from "react";
import { useLocale } from "lib/hooks/useLocale";
import { FormField } from "lib/hooks/useForm";
import { AddressCard } from "components/address/cards/AddressCard";
import { DialogFooter } from "components/common/dialogs/GenericDialog";
import { useUserContext } from "lib/contexts/UserProvider";
import { AddressCardFields, AddressErrorType, PartialAddressPayloadType } from "lib/types/address";
import { putRequest, getRequest } from "lib/core/apiClient";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { handleOutOfStockResponse } from "lib/utils/checkout";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { AddressSource, analyticsEvents, apiURI } from "lib/utils/constants";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { ShippingHandlesType, CheckoutActions, CheckoutErrorResponse } from "lib/types/checkout";
import { errorToast } from "lib/utils/toasters";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { classNames, redirectUrl, scrollToID } from "lib/utils/helpers";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { mutate } from "swr";
import { getDetailsByZip } from "lib/core/apiMethods";
import {
  checkIfHasServiceabilityError,
  useCheckoutErrors,
  getServiceabilityErrorMessage,
} from "lib/hooks/useCheckoutErrors";
import { AnimateLoading } from "components/animation/AnimateLoading";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import { validateAddressFields } from "lib/utils/address-validation";
import { filterDisabledAddressFields } from "lib/utils/address";
import { CtaConfig } from "lib/types/merchantConfigTypes";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

interface AddressModifyProps {
  setIsOpen?: () => void;
  isPrimaryBtnLoading?: boolean;
  handleEditAddress: (id: string) => void;
  defaultShowAllAddresses?: boolean;
}

const AddressModify: React.FC<AddressModifyProps> = ({
  setIsOpen,
  isPrimaryBtnLoading = false,
  handleEditAddress,
  defaultShowAllAddresses = false,
}) => {
  const {
    actions: { mutatePayment },
  } = usePaymentContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAllAddresses, setShowAllAddresses] = useState<boolean>(false);
  const [selectedAddrId, setSelectedAddrId] = useState<string>("");
  const {
    state: { addressConfig, merchant, isValidating, checkoutMetadata },
  } = useMerchantContext();

  const { t } = useLocale();
  const {
    state: { user },
    actions: { setUserAddress },
  } = useUserContext();
  const {
    state: { checkoutId, isC2P, actionUrls, checkoutValidations, checkoutLoading },
    actions: {
      setCheckoutModal,
      setCheckoutView,
      setShippingHandles,
      setOOSItems,
      updateCheckoutBasedOnCheckoutResponse,
      setCheckoutValidations,
    },
  } = useCheckoutContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { openPopup, closePopup } = usePopupOrchestrator();
  const [addressErrors, setAddressErrors] = useState<AddressErrorType>({});
  const { triggerCheckoutErrorInfoToast } = useCheckoutErrors();
  const [searchParams] = useSearchParams();
  const { initialCheckoutStep } = checkoutMetadata ?? {};

  const isTokenV2Checkout = !Boolean(searchParams.get("shop_url"));

  const shippingAddress = useMemo(() => {
    const addresses = user?.addresses?.filter(
      (address: AddressCardFields) => address?.source === "SHIPPING_ADDRESS",
    );
    return addresses;
  }, [user?.addresses, showAllAddresses]);

  useEffect(() => {
    if (Boolean(isC2P) || !user?.uid || !checkoutId) return;
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_ADDRESSES_LIST_LOADED,
      eventType: "load",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isC2P, checkoutId]);

  useEffect(() => {
    setSelectedAddrId(user?.default_shipping_address?.id ?? "");
  }, [user]);

  const updateDefaultAddress = async () => {
    const selectedAddress = user?.addresses?.find(
      (address: AddressCardFields) => address?.id === selectedAddrId,
    );
    if (!selectedAddress) {
      setSelectedAddrId("ADDRESS_NEW");
      openPopup("ADDRESS_NEW", {});
      return;
    }
    const addressValidity = validateAddressFields({
      address: selectedAddress,
      config: addressConfig,
      checkoutId,
    });

    if (!addressValidity.available) {
      setCheckoutValidations({
        ...checkoutValidations,
        address: {
          isValid: false,
          metadata: {
            addressId: selectedAddrId,
            fieldData: addressValidity?.errorFieldData,
          },
        },
      });
      handleEditAddress(selectedAddrId);
      return;
    }
    setCheckoutValidations({
      ...checkoutValidations,
      address: {
        isValid: true,
      },
    });

    if (!Boolean(user?.addresses) && initialCheckoutStep !== "PAYMENTS") {
      openPopup("ADDRESS_NEW", {});
      return;
    }

    if (!Boolean(selectedAddrId)) {
      errorToast(t("address_not_selected"));
      return;
    }

    let addressPayload: PartialAddressPayloadType | {} = {};
    if (!Boolean(selectedAddress?.state_code)) {
      try {
        const stateCodeResponse = await getDetailsByZip(selectedAddress?.zip ?? "");
        const filteredAddress = filterDisabledAddressFields(
          { ...selectedAddress, state_code: stateCodeResponse?.state_code },
          addressConfig,
        );
        const tempPayload: PartialAddressPayloadType = {
          account_id: user?.uid,
          source: AddressSource.SHIPPING_ADDRESS,
          address_type: selectedAddress?.type || "OTHER",
          address_data: filteredAddress,
        };
        addressPayload = { ...tempPayload };
      } catch (e) {
        console.error(e);
      }
    }

    try {
      setIsLoading(true);
      const checkoutResponse: any = await putRequest(
        `/checkout/${checkoutId}/address/${selectedAddrId}`,
        addressPayload,
        "KRATOS_PRIVATE",
      );
      const accountResponse = await getRequest("/gatekeeper/accounts?include=address", "KRATOS_PRIVATE");
      handleResponseSuccess(checkoutResponse, accountResponse);
      setIsLoading(false);

      const showShippingHandles = checkoutResponse?.metadata?.show_shipping_handle_selector ?? false;
      const shippingHandles = checkoutResponse?.metadata?.available_shipping_handles ?? [];

      const isNotServicable =
        checkIfHasServiceabilityError(
          checkoutResponse?.metadata?.error_responses as CheckoutErrorResponse[],
        ) || shippingHandles.length === 0;

      if (checkoutResponse?.metadata?.error_response?.code === "OUT_OF_STOCK") {
        handleOutOfStockResponse({
          items: checkoutResponse?.metadata?.error_response?.metadata?.items,
          addons: checkoutResponse?.metadata?.error_response?.metadata?.addons,
          setOOSItems,
          openPopup,
          sendAnalyticsEvent,
        });
        return;
      }

      if (isNotServicable && initialCheckoutStep !== "PAYMENTS") {
        scrollToID("flo_add__address");
        const errorMessage = getServiceabilityErrorMessage(
          checkoutResponse?.metadata?.error_responses as CheckoutErrorResponse[],
          { user },
        );
        triggerCheckoutErrorInfoToast(errorMessage);
        return;
      }

      if (isNotServicable && initialCheckoutStep === "PAYMENTS") {
        const addressErr = { ...addressErrors };
        addressErr[selectedAddrId] = {
          hasError: true,
          // todo: shradhan check if this is correct
          errorMessage: t("pincode_not_serviceable_item_in_cart"),
        };
        setAddressErrors(addressErr);
        triggerCheckoutErrorInfoToast(t("serviceability_error"));
        scrollToID("flo_add__address");
      }

      setShippingHandles(shippingHandles as ShippingHandlesType);
      if (shippingHandles.length > 0 && Boolean(showShippingHandles) && initialCheckoutStep !== "PAYMENTS") {
        openPopup("SHIPPING_HANDLES", {});
        return;
      }
      if (initialCheckoutStep === "PAYMENTS") {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_SHIPPING_HANDLES_LOADED,
          eventType: "load",
          metaData: {
            shippingData: {
              availableHandles: shippingHandles,
            },
          },
        });
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(`UPI_INTENT`);
      }

      if (checkoutResponse?.metadata?.action_urls?.[CheckoutActions.ADDRESS_SELECT]?.success_url) {
        redirectUrl(checkoutResponse.metadata.action_urls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }

      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].success_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }
      if (!isNotServicable) {
        setCheckoutModal("NONE");
        closePopup();
      }
      setCheckoutView("PAYMENTS");
    } catch (e: any) {
      console.error(e);
      const error = e?.response?.data?.error;
      errorToast(error?.message ?? t("something_went_wrong"));
      setIsLoading(false);

      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].failure_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].failure_url);
      }

      // On BE error, open the edit address popup so the user can fix the address.
      if (selectedAddrId) {
        // Highlight the offending field in the edit form using the field returned by BE.
        const errorField = error?.data?.field;
        if (errorField && Object.values(FormField).includes(errorField)) {
          setCheckoutValidations({
            ...checkoutValidations,
            address: {
              isValid: false,
              metadata: {
                addressId: selectedAddrId,
                fieldData: {
                  [errorField]: { status: true, message: error?.message ?? "" },
                },
              },
            },
          });
        }
        handleEditAddress(selectedAddrId);
      }
    }
  };

  const handleResponseSuccess = (checkoutResponse: any, accountResponse: any) => {
    handleCheckoutResponse(checkoutResponse);
    handleAccountResponse(accountResponse, checkoutResponse);
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_ADDRESS_SELECTED,
      eventType: "click",
    });
  };

  const handleCheckoutResponse = (checkoutResponse: any) => {
    updateCheckoutBasedOnCheckoutResponse(checkoutResponse);
  };

  const handleAccountResponse = (accountResponse: any, checkoutResponse: any) => {
    setUserAddress(checkoutResponse?.addresses, accountResponse?.addresses, undefined, addressConfig);
  };

  const getPrimaryButtonText = () => {
    if (isPrimaryBtnLoading) return "";
    if (user?.uid && !Boolean(user?.addresses)) return t("add_new_address_button");
    const cta = checkoutMetadata?.primaryCTAConfig?.find(
      (config: CtaConfig) => config.type === "ORDER_SUMMARY",
    )?.ctaText;
    return cta;
  };

  const addressListSkeleton = (
    <div className="space-y-3">
      {new Array(3).fill(null).map((_, index) => (
        <div className="px-3 py-2 space-y-2" key={index}>
          <div className="skeleton-loader h-[16px] rounded-md w-2/5"></div>
          <div className="skeleton-loader h-[16px] rounded-md w-full"></div>
        </div>
      ))}
    </div>
  );

  const getExpandedWidget = () => {
    const text = showAllAddresses ? "View less" : "View more";
    const icon = showAllAddresses ? <CaretUp size={18} /> : <CaretDown size={18} />;
    return (
      <div
        className="flex justify-end items-center mt-2"
        onClick={() => setShowAllAddresses((prev) => !prev)}>
        <div className="flex justify-center items-center hover:bg-gray-lighter p-2 rounded-md cursor-pointer w-fit space-x-1">
          <span className="text-sm font-medium text-coal-dark">{text}</span>
          {icon}
        </div>
      </div>
    );
  };

  const getAddressList = () => {
    const splicedAddress =
      showAllAddresses || defaultShowAllAddresses ? shippingAddress : shippingAddress?.slice(0, 2);

    if (!splicedAddress?.length) {
      return (
        <div className="flex justify-center items-center w-full py-2 bg-gray-lighter rounded-xl">
          <span className="text-sm font-medium text-gray-dark">{t("no_saved_addresses")}</span>
        </div>
      );
    }

    return (
      <ul className={classNames("flex flex-col space-y-2")}>
        {splicedAddress?.map((address: AddressCardFields, index: number) => (
          <li
            id={`flo__address__address${index + 1}`}
            key={address?.id}
            onClick={(e) => {
              e.preventDefault();
              setSelectedAddrId(address?.id ?? "");
            }}>
            <AddressCard
              {...address}
              setAddress={() => setSelectedAddrId(address?.id ?? "")}
              isChecked={selectedAddrId === address?.id}
              selectedAddrId={selectedAddrId}
              handleEditAddress={handleEditAddress}
              defaultShippingAddress={user?.default_shipping_address?.id ?? ""}
              defaultBillingAddress={user?.default_billing_address?.id ?? ""}
              errorMessage={
                address?.id
                  ? Boolean(addressErrors[address?.id]?.hasError)
                    ? addressErrors[address?.id].errorMessage
                    : ""
                  : ""
              }
            />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <AnimateLoading loading={checkoutLoading}>
        <AnimateLoading.Skeleton>{addressListSkeleton}</AnimateLoading.Skeleton>
        <AnimateLoading.Content>
          {getAddressList()}
          {!defaultShowAllAddresses && shippingAddress && shippingAddress.length > 2 && getExpandedWidget()}
        </AnimateLoading.Content>
      </AnimateLoading>
      <DialogFooter>
        <PrimaryButton
          isDisabled={isValidating && !isTokenV2Checkout}
          buttonText={getPrimaryButtonText()}
          onClick={updateDefaultAddress}
          height="h-14"
          isCheckout={true}
          isLoading={isPrimaryBtnLoading || isLoading}
          id="flo_address__btn"
        />
      </DialogFooter>

      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default React.memo(AddressModify);
